import React, { forwardRef, useEffect, useImperativeHandle, useState, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { Edit2, Trash2, Plus, RefreshCw, Search, Package } from 'react-feather';
import menuApi from 'api/requests/menu';
import BottomSheet from 'components/_UI/BottomSheet';
import ProductForm from '../forms/ProductForm';
import ConfirmDialog from '../components/ConfirmDialog';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';

function formatPrice(value) {
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default forwardRef(function ProductsTab({ slug, toast }, ref) {
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sheet, setSheet] = useState({ open: false, data: null });
    const [confirm, setConfirm] = useState({ open: false, id: null, name: '' });
    const [deleting, setDeleting] = useState(false);

    useImperativeHandle(ref, () => ({
        openCreate() { setSheet({ open: true, data: null }); },
        updateCategories(cats) { setCategories(cats); },
    }));

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        setError(null);
        try {
            const [catRes, prodRes] = await Promise.all([
                menuApi.getCategories(slug),
                menuApi.getProducts(slug),
            ]);
            setCategories(catRes.data.data || []);
            setProducts(prodRes.data.data || []);
        } catch {
            setError('Não foi possível carregar os produtos.');
        } finally {
            setLoading(false);
        }
    }

    function requestDelete(product) {
        setConfirm({ open: true, id: product.id, name: product.name });
    }

    async function handleConfirmDelete() {
        setDeleting(true);
        try {
            await menuApi.deleteProduct(slug, confirm.id);
            setProducts(prev => prev.filter(p => p.id !== confirm.id));
            toast?.success('Produto removido com sucesso');
        } catch {
            toast?.error('Erro ao remover produto');
        } finally {
            setDeleting(false);
            setConfirm({ open: false, id: null, name: '' });
        }
    }

    function handleSaved(saved) {
        const isEdit = products.find(p => p.id === saved.id);
        setProducts(prev => {
            const exists = prev.find(p => p.id === saved.id);
            return exists ? prev.map(p => p.id === saved.id ? saved : p) : [...prev, saved];
        });
        toast?.success(isEdit ? 'Produto atualizado' : 'Produto criado com sucesso');
        setSheet({ open: false, data: null });
    }

    const filtered = useMemo(() => {
        let result = products;
        if (selectedCategory) {
            result = result.filter(p => p.menu_category_id === selectedCategory);
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            result = result.filter(p =>
                p.name.toLowerCase().includes(q) ||
                (p.description && p.description.toLowerCase().includes(q))
            );
        }
        return result;
    }, [products, selectedCategory, searchQuery]);

    function getCategoryCount(catId) {
        return products.filter(p => p.menu_category_id === catId).length;
    }

    if (loading) return <LoadingContainer><Spinner /><LoadingText>Carregando produtos...</LoadingText></LoadingContainer>;

    if (error) {
        return (
            <ErrorState>
                <ErrorIcon><RefreshCw size={24} color={colors.danger} /></ErrorIcon>
                <ErrorTitle>{error}</ErrorTitle>
                <RetryBtn onClick={load} aria-label='Tentar novamente'>
                    <RefreshCw size={14} /> Tentar novamente
                </RetryBtn>
            </ErrorState>
        );
    }

    return (
        <Container>
            {/* Search bar */}
            {products.length > 0 && (
                <SearchBar>
                    <SearchIcon><Search size={16} color='#999' /></SearchIcon>
                    <SearchInput
                        type='text'
                        placeholder='Buscar produtos...'
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        aria-label='Buscar produtos'
                    />
                </SearchBar>
            )}

            {/* Category filter */}
            {categories.length > 0 && products.length > 0 && (
                <FilterBar role='group' aria-label='Filtrar por categoria'>
                    <FilterChip
                        $active={!selectedCategory}
                        onClick={() => setSelectedCategory(null)}
                        aria-pressed={!selectedCategory}
                    >
                        Todos <Count $active={!selectedCategory}>{products.length}</Count>
                    </FilterChip>
                    {categories.map(cat => (
                        <FilterChip
                            key={cat.id}
                            $active={selectedCategory === cat.id}
                            onClick={() => setSelectedCategory(cat.id)}
                            aria-pressed={selectedCategory === cat.id}
                        >
                            {cat.name} <Count $active={selectedCategory === cat.id}>{getCategoryCount(cat.id)}</Count>
                        </FilterChip>
                    ))}
                </FilterBar>
            )}

            {/* Product grid/list */}
            {products.length === 0 ? (
                <EmptyState>
                    <EmptyIconWrapper><Package size={32} color={colors.occasionalPurple} /></EmptyIconWrapper>
                    <EmptyText>Nenhum produto cadastrado</EmptyText>
                    <EmptyHint>Adicione produtos ao seu cardápio digital</EmptyHint>
                    <EmptyCTA onClick={() => setSheet({ open: true, data: null })}>
                        <Plus size={16} /> Criar primeiro produto
                    </EmptyCTA>
                </EmptyState>
            ) : filtered.length === 0 ? (
                <EmptyState>
                    <EmptyText>Nenhum produto encontrado</EmptyText>
                    <EmptyHint>Tente alterar o filtro ou o termo de busca</EmptyHint>
                </EmptyState>
            ) : (
                <ProductGrid>
                    {filtered.map(product => (
                        <ProductCard key={product.id}>
                            <CardImage>
                                {product.image
                                    ? <img src={product.image.thumb} alt={product.name}
                                        onError={e => { e.target.onerror = null; e.target.src = '/assets/images/placeholder-events.png'; }} />
                                    : <ImgPlaceholder />
                                }
                            </CardImage>
                            <CardBody>
                                <ProductName>{product.name}</ProductName>
                                {product.description && <ProductDesc title={product.description}>{product.description}</ProductDesc>}
                                <ProductPrice>{formatPrice(product.base_price)}</ProductPrice>
                            </CardBody>
                            <CardActions>
                                <ActionBtn
                                    onClick={() => setSheet({ open: true, data: product })}
                                    aria-label={`Editar produto ${product.name}`}
                                >
                                    <Edit2 size={15} color={colors.night} />
                                </ActionBtn>
                                <ActionBtn
                                    onClick={() => requestDelete(product)}
                                    aria-label={`Remover produto ${product.name}`}
                                >
                                    <Trash2 size={15} color={colors.danger} />
                                </ActionBtn>
                            </CardActions>
                        </ProductCard>
                    ))}
                </ProductGrid>
            )}

            <BottomSheet
                isOpen={sheet.open}
                onClose={() => setSheet({ open: false, data: null })}
                title={sheet.data ? 'Editar produto' : 'Novo produto'}
                size='large'
            >
                <ProductForm
                    slug={slug}
                    categories={categories}
                    initialData={sheet.data}
                    onSaved={handleSaved}
                    onCancel={() => setSheet({ open: false, data: null })}
                />
            </BottomSheet>

            <ConfirmDialog
                isOpen={confirm.open}
                title='Remover produto'
                message={`Tem certeza que deseja remover "${confirm.name}"? Esta ação não pode ser desfeita.`}
                confirmLabel='Remover'
                danger
                loading={deleting}
                onConfirm={handleConfirmDelete}
                onCancel={() => setConfirm({ open: false, id: null, name: '' })}
            />
        </Container>
    );
});

const Container = styled.div`
    padding: 20px 16px 24px;
    position: relative;
    min-height: 100%;
`;

const SearchBar = styled.div`
    display: flex;
    align-items: center;
    background: #fff;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 0 12px;
    margin-bottom: 12px;
    transition: border-color 0.15s;
    &:focus-within { border-color: ${colors.occasionalPurple}; }
`;

const SearchIcon = styled.div`
    display: flex;
    align-items: center;
    margin-right: 8px;
    flex-shrink: 0;
`;

const SearchInput = styled.input`
    flex: 1;
    border: none;
    padding: 11px 0;
    font-size: 14px;
    outline: none;
    color: ${colors.nightDark};
    background: transparent;
    &::placeholder { color: #999; }
`;

const FilterBar = styled.div`
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 12px;
    margin-bottom: 8px;
    -webkit-overflow-scrolling: touch;
    &::-webkit-scrollbar { display: none; }
`;

const FilterChip = styled.button`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 7px 14px;
    border-radius: 20px;
    border: 1.5px solid ${({ $active }) => $active ? colors.occasionalPurple : colors.neutralDark};
    background: ${({ $active }) => $active ? colors.occasionalPurple : '#fff'};
    color: ${({ $active }) => $active ? '#fff' : '#666'};
    font-size: 13px;
    font-weight: ${({ $active }) => $active ? '600' : '500'};
    cursor: pointer;
    transition: all 0.15s;
    &:focus-visible { outline: 2px solid ${colors.occasionalPurple}; outline-offset: 2px; }
`;

const Count = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    border-radius: 9px;
    background: ${({ $active }) => $active ? 'rgba(255,255,255,0.3)' : colors.neutralDark};
    font-size: 11px;
    font-weight: 700;
    padding: 0 4px;
`;

const ProductGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;

    @media screen and (min-width: ${breakpoints.md}) {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
    }
`;

const ProductCard = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
    border: 1px solid ${colors.neutralDark};
    transition: box-shadow 0.15s;
    &:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.12); }
`;

const CardImage = styled.div`
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    border-radius: 8px;
    overflow: hidden;
    img { width: 100%; height: 100%; object-fit: cover; }
`;

const ImgPlaceholder = styled.div`
    width: 100%;
    height: 100%;
    background: ${colors.neutralDark};
    border-radius: 8px;
`;

const CardBody = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
`;

const ProductName = styled.span`
    font-weight: 700;
    font-size: 15px;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ProductDesc = styled.span`
    font-size: 12px;
    color: #666;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ProductPrice = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
`;

const CardActions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ActionBtn = styled.button`
    background: none;
    border: none;
    padding: 6px;
    cursor: pointer;
    border-radius: 6px;
    display: flex;
    align-items: center;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
    &:focus-visible { outline: 2px solid ${colors.occasionalPurple}; outline-offset: 2px; }
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
    gap: 12px;
`;

const LoadingText = styled.span`
    font-size: 13px;
    color: #666;
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const Spinner = styled.div`
    width: 32px; height: 32px;
    border: 3px solid ${colors.neutralDark};
    border-top-color: ${colors.occasionalPurple};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

const EmptyState = styled.div`
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 60px 20px; text-align: center;
`;

const EmptyIconWrapper = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: ${colors.occasionalPurple}10;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
`;

const EmptyText = styled.p`font-weight: 700; font-size: 16px; color: ${colors.nightDark}; margin: 0 0 6px;`;
const EmptyHint = styled.p`font-size: 13px; color: #666; margin: 0 0 18px;`;

const EmptyCTA = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    border-radius: 10px;
    background: ${colors.occasionalPurple};
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
`;

const ErrorState = styled.div`
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 60px 20px; text-align: center; gap: 8px;
`;

const ErrorIcon = styled.div`
    width: 48px; height: 48px; border-radius: 50%; background: ${colors.danger}10;
    display: flex; align-items: center; justify-content: center; margin-bottom: 8px;
`;

const ErrorTitle = styled.p`font-weight: 600; font-size: 15px; color: ${colors.nightDark}; margin: 0;`;

const RetryBtn = styled.button`
    display: flex; align-items: center; gap: 6px; padding: 10px 20px; border-radius: 8px;
    background: none; border: 1.5px solid ${colors.neutralDark}; color: ${colors.nightDark};
    font-size: 13px; font-weight: 600; cursor: pointer; margin-top: 8px;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
`;
